import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * The original transparent attribute value, stored when component initializes
   * Used to restore the attribute when scrolling back to top
   * @type {string | null}
   */
  #originalTransparentValue = null;

  /**
   * The header height when transparent, captured before removing transparent attribute
   * Used to maintain consistent header height when transitioning from transparent to opaque
   * @type {number | null}
   */
  #transparentHeaderHeight = null;

  /**
   * Flag to prevent ResizeObserver from updating height when restoring transparent
   * @type {boolean}
   */
  #isRestoringTransparent = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    // Don't update header height if desktop drawer is open (to prevent layout shift)
    // Check if the desktop drawer details element has the 'open' attribute
    const desktopDrawerDetails = document.querySelector('header-drawer.header__drawer--desktop details[open]');
    const isDrawerOpen = !!desktopDrawerDetails;
    
    if (!isDrawerOpen) {
      // Don't update if we're in the process of restoring transparent
      if (this.#isRestoringTransparent) {
        return;
      }

      const { height } = entry.target.getBoundingClientRect();
      
      // If header is transparent, store the height for later use
      // Only capture it once when first transparent (before any drawer interactions)
      if (this.hasAttribute('transparent')) {
        // Only store if we don't have one yet - this captures the "clean" transparent height
        if (this.#transparentHeaderHeight === null) {
          this.#transparentHeaderHeight = height;
        }
      }
      
      // Always use stored transparent height when header is transparent
      // Use stored transparent height when header is opaque (to maintain consistency)
      // Otherwise use current height
      let heightToUse = height;
      if (this.#transparentHeaderHeight !== null) {
        if (this.hasAttribute('transparent')) {
          // Header is transparent, always use stored height
          heightToUse = this.#transparentHeaderHeight;
        } else {
          // Header is opaque, use stored transparent height to maintain consistency
          heightToUse = this.#transparentHeaderHeight;
        }
      }
      
      document.body.style.setProperty('--header-height', `${heightToUse}px`);
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = scrollTop <= 0;
      const hasTransparent = this.hasAttribute('transparent');

      // Remove transparent attribute when scrolled down, restore when at top
      if (isAtTop) {
        // Restore transparent attribute when scrolled back to top
        if (!hasTransparent && this.#originalTransparentValue) {
          // Set flag to prevent ResizeObserver from updating during restoration
          this.#isRestoringTransparent = true;
          
          // If we have a stored transparent height, use it immediately to prevent jump
          if (this.#transparentHeaderHeight !== null) {
            document.body.style.setProperty('--header-height', `${this.#transparentHeaderHeight}px`);
          }
          
          this.setAttribute('transparent', this.#originalTransparentValue);
          
          // Clear flag after a brief delay to allow attribute to be set
          requestAnimationFrame(() => {
            this.#isRestoringTransparent = false;
          });
        }
      } else {
        // Remove transparent attribute when scrolled down (any amount)
        if (hasTransparent) {
          this.removeAttribute('transparent');
        }
      }

      const headerTop = this.getBoundingClientRect().top;
      if (headerTop >= 0) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Store original transparent value if it exists
    if (this.hasAttribute('transparent')) {
      this.#originalTransparentValue = this.getAttribute('transparent');
      // Capture the transparent height immediately on load, before any drawer interactions
      const initialHeight = this.getBoundingClientRect().height;
      this.#transparentHeaderHeight = initialHeight;
      // Set it immediately to ensure consistency
      document.body.style.setProperty('--header-height', `${initialHeight}px`);
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
        
        // Check initial scroll position for transparent headers
        if (stickyMode === 'always' && this.#originalTransparentValue) {
          const initialScrollTop = document.scrollingElement?.scrollTop ?? 0;
          const isAtTop = initialScrollTop <= 0;
          if (!isAtTop) {
            // Page is already scrolled, remove transparent attribute
            this.removeAttribute('transparent');
          }
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Check if header started with transparent attribute - if so, we should NOT
  // dynamically update header-group-height during scroll (to prevent slideshow jumping)
  const hasTransparentHeader = header instanceof HTMLElement && header.hasAttribute('transparent');

  // Add persistent class for CSS to use (stays even when transparent attr is removed)
  if (hasTransparentHeader) {
    document.body.classList.add('has-transparent-header');
  }

  // Function to update header group height
  const updateHeaderGroupHeight = () => {
    // For transparent header pages, don't update dynamically (prevents slideshow jumping)
    if (hasTransparentHeader) return;

    // Don't update header group height if desktop drawer is open (to prevent layout shift)
    const desktopDrawerDetails = document.querySelector('header-drawer.header__drawer--desktop details[open]');
    const isDrawerOpen = !!desktopDrawerDetails;

    if (!isDrawerOpen) {
      const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    }
  };

  // Update header group height on resize of any child (only for non-transparent headers)
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => updateHeaderGroupHeight());

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });

    // Only update on scroll for NON-transparent header pages
    // For transparent headers, we keep the initial value to prevent slideshow jumping
    if (!hasTransparentHeader) {
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let scrollTimeout;

      window.addEventListener('scroll', () => {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        scrollTimeout = window.setTimeout(() => updateHeaderGroupHeight(), 100);
      }, { passive: true });
    }
  }
});
